"use client";

import { useState, Fragment } from "react";
import Link from "next/link";
import {
  ChevronDown,
  ChevronRight,
  Wrench,
  ExternalLink,
  AlertTriangle,
  ArrowRight,
  RotateCcw,
  BarChart3,
  FileText,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Send,
  X,
  ChevronUp,
  Brain,
  Paperclip,
  Volume2,
  Square,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ToolCallLog, ModelDowngradeInfo, UngroundedInfo } from "@/lib/types";
import { deriveToolCallView, isSafeHttpUrl, isSafeMediaSrc } from "@/lib/tool-call-view";
import { humanizeToolSummary } from "@/lib/humanize-error";
import { useSpeech, stripMarkdownForSpeech } from "@/lib/speech";
import { sortVoicesByQuality, voiceQuality } from "@/lib/speech-store";
import {
  OrchestrationPlanCard,
  type OrchestrationPlanCardData,
} from "./orchestration-plan-card";
import { ApprovalCard } from "./approval-card";
import type { PendingDecision } from "./message-list";

// ─── Voice grouping (read-aloud picker) ─────────────────────────────────────

function groupVoicesByLanguage(
  voices: SpeechSynthesisVoice[],
): [string, SpeechSynthesisVoice[]][] {
  const groups: Record<string, SpeechSynthesisVoice[]> = {};
  for (const voice of voices) {
    const lang = voice.lang || "Other";
    if (!groups[lang]) groups[lang] = [];
    groups[lang].push(voice);
  }

  // The device's own language first, then the rest alphabetically. A user
  // scrolling past thirty Spanish voices to find English is the common case on
  // a phone with several voices installed.
  const mine = (typeof navigator !== "undefined" ? navigator.language : "en")
    .slice(0, 2)
    .toLowerCase();
  return Object.entries(groups)
    .map(([lang, list]) => [lang, sortVoicesByQuality(list)] as [string, SpeechSynthesisVoice[]])
    .sort(([a], [b]) => {
      const aMine = a.slice(0, 2).toLowerCase() === mine;
      const bMine = b.slice(0, 2).toLowerCase() === mine;
      if (aMine !== bMine) return aMine ? -1 : 1;
      return a.localeCompare(b);
    });
}

// ─── Category icon map ───────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "market report": <TrendingUp className="size-3" />,
  competitive: <BarChart3 className="size-3" />,
  financials: <FileText className="size-3" />,
  research: <FileText className="size-3" />,
  analysis: <BarChart3 className="size-3" />,
};

function getCategoryIcon(category: string): React.ReactNode {
  const key = category.toLowerCase();
  for (const [k, icon] of Object.entries(CATEGORY_ICONS)) {
    if (key.includes(k)) return icon;
  }
  return <FileText className="size-3" />;
}

// ─── Simple markdown renderer ────────────────────────────────────────────────

export function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;
  // Use a monotonic counter for keys instead of the line index `i`. Block
  // handlers (bullet/numbered list, code block) advance `i` past their range
  // before pushing the wrapping node, which previously caused the wrapper's
  // `key={i}` to collide with the next paragraph's `key={i}` on the same
  // line — fired the React duplicate-key warning in the wild on 2026-05-02.
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(
        <pre
          key={`md-${k++}`}
          className="my-3 rounded-lg bg-[var(--surface-lowest)] border border-[var(--outline)] overflow-x-auto"
        >
          {lang && (
            <div className="px-4 py-1.5 border-b border-[var(--outline)] text-[10px] font-mono uppercase text-[var(--on-surface-variant)]">
              {lang}
            </div>
          )}
          <code className="block px-4 py-3 text-xs font-mono text-[var(--on-surface)] leading-relaxed whitespace-pre">
            {codeLines.join("\n")}
          </code>
        </pre>
      );
      i++;
      continue;
    }

    // Heading
    if (line.startsWith("### ")) {
      nodes.push(
        <h3 key={`md-${k++}`} className="text-sm font-bold text-[var(--on-surface)] mt-4 mb-1">
          {line.slice(4)}
        </h3>
      );
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      nodes.push(
        <h2 key={`md-${k++}`} className="text-base font-bold text-[var(--on-surface)] mt-4 mb-1">
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    // Bullet list
    if (line.startsWith("- ") || line.startsWith("* ")) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
        items.push(lines[i].slice(2));
        i++;
      }
      nodes.push(
        <ul key={`md-${k++}`} className="my-2 space-y-1 pl-4">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-[var(--on-surface)] flex gap-2 leading-relaxed">
              <span className="text-[var(--primary)] mt-1.5 shrink-0 text-[0.4rem]">●</span>
              <span>{inlineFormat(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      nodes.push(
        <ol key={`md-${k++}`} className="my-2 space-y-1 pl-4 list-decimal list-inside">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-[var(--on-surface)] leading-relaxed">
              {inlineFormat(item)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line → spacer
    if (line.trim() === "") {
      nodes.push(<div key={`md-${k++}`} className="h-2" />);
      i++;
      continue;
    }

    // Markdown media: ![alt](url) — image by default. If the alt starts with
    // a kind prefix — video: / audio: / image: — render the matching element
    // instead. Tools (generate_image, generate_video, generate_audio) emit
    // this pattern so rendering is uniform across modalities.
    const mediaMatch = line.match(/^!\[(?:(image|video|audio):)?(.*?)\]\((\S+?)\)\s*$/i);
    if (mediaMatch) {
      const kind = (mediaMatch[1] || 'image').toLowerCase();
      const alt = mediaMatch[2] || '';
      // Assistant content is web-search-influenced AND now persisted, so a
      // `![x](javascript:...)` renders a live javascript: anchor on every
      // reload. rel="noopener" does nothing for that. data:/blob: are allowed
      // for generated media, which is what these tools legitimately emit.
      const rawSrc = mediaMatch[3];
      // Root-relative same-origin (`/api/artifacts/<id>/content`) is what the
      // media tools actually emit. `//host/x` is protocol-relative and resolves
      // CROSS-origin, so it must not pass — hence the negative lookahead.
      // Both checks are kept: the scheme test here and isSafeMediaSrc, which
      // re-validates independently.
      const sameOriginPath = /^\/(?!\/)/.test(rawSrc);
      const src =
        (sameOriginPath || /^(https?:|data:|blob:)/i.test(rawSrc)) && isSafeMediaSrc(rawSrc)
          ? rawSrc
          : null;
      if (!src) {
        nodes.push(
          <p key={`md-${k++}`} className="text-sm text-[var(--on-surface)] leading-relaxed">
            {inlineFormat(line)}
          </p>
        );
        i++;
        continue;
      }
      const wrapClass = 'block my-2 rounded-lg overflow-hidden border border-[var(--outline)] bg-[var(--surface-lowest)] hover:border-[var(--primary)] transition-colors max-w-lg';

      if (kind === 'video') {
        nodes.push(
          <div key={`md-${k++}`} className={wrapClass}>
            <video
              src={src}
              controls
              preload="metadata"
              className="w-full h-auto block"
              aria-label={alt || 'Generated video'}
            />
            {alt && (
              <div className="px-3 py-1.5 text-[11px] text-on-surface-muted border-t border-[var(--outline)]">
                {alt}
              </div>
            )}
          </div>
        );
      } else if (kind === 'audio') {
        nodes.push(
          <div key={`md-${k++}`} className="my-2 rounded-lg border border-[var(--outline)] bg-[var(--surface-lowest)] p-3 max-w-lg">
            {alt && <div className="text-xs text-on-surface-variant mb-2">{alt}</div>}
            <audio src={src} controls className="w-full" aria-label={alt || 'Generated audio'} />
          </div>
        );
      } else {
        // image (default)
        nodes.push(
          <a
            key={`md-${k++}`}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className={wrapClass}
          >
            {/* Using <img> directly (not next/image) because these are dynamic,
                user-generated URLs that don't need Next.js image optimization. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={alt || 'Generated image'}
              className="w-full h-auto block"
              loading="lazy"
            />
          </a>
        );
      }
      i++;
      continue;
    }

    // Regular paragraph
    nodes.push(
      <p key={`md-${k++}`} className="text-sm text-[var(--on-surface)] leading-relaxed">
        {inlineFormat(line)}
      </p>
    );
    i++;
  }

  return nodes;
}

/** Shared anchor styling + safety attributes for any link we render inline. */
function inlineLink(href: string, label: string, key: number): React.ReactNode {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--primary)] underline underline-offset-2 hover:opacity-80 break-all"
    >
      {label}
    </a>
  );
}

function inlineFormat(text: string): React.ReactNode {
  // Bold + inline code + links.
  //
  // LINKS ADDED 2026-09-17. This handled bold and code only, so an agent that
  // replied with a URL produced text the user had to select and copy by hand.
  // That is not a cosmetic gap: the Composio connect flow ends in the agent
  // handing over a one-time authorisation link, and an unclickable link at that
  // moment reads as a dead end — which is exactly how it was reported.
  //
  // Two forms are handled, because agents produce both:
  //   [label](url)  — markdown, what a well-behaved reply emits
  //   https://…     — bare, what replies actually emit about half the time
  //
  // Every href goes through isSafeHttpUrl (a real URL parse, not a pattern
  // match), so javascript:, data: and vbscript: cannot become an anchor here.
  // A URL that fails the check renders as plain text rather than disappearing —
  // the user can still see and copy it, which beats silently dropping content.
  const parts = text.split(
    /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]\n]+\]\([^)\s]+\)|https?:\/\/[^\s<>()[\]]+)/g,
  );
  return parts.map((part, i) => {
    if (!part) return part;

    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-[var(--on-surface)]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-[var(--surface-lowest)] text-[var(--primary)] text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }

    // [label](url)
    const md = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/.exec(part);
    if (md) {
      const [, label, href] = md;
      return isSafeHttpUrl(href) ? inlineLink(href, label, i) : part;
    }

    // Bare URL. Trailing sentence punctuation is not part of the link —
    // "see https://x.com/a." would otherwise 404 on the full stop.
    if (/^https?:\/\//.test(part)) {
      const trailing = /[.,;:!?)\]]+$/.exec(part)?.[0] ?? "";
      const href = trailing ? part.slice(0, -trailing.length) : part;
      if (!isSafeHttpUrl(href)) return part;
      return (
        <Fragment key={i}>
          {inlineLink(href, href, i)}
          {trailing}
        </Fragment>
      );
    }

    return part;
  });
}

// ─── Tool call card (Stitch style) ──────────────────────────────────────────

function ToolCallCard({ call }: { call: ToolCallLog }) {
  const [expanded, setExpanded] = useState(false);

  // Derivation lives in src/lib/tool-call-view.ts and is total — it never
  // throws for any shape. A rehydrated call has no `input` (never persisted)
  // and often no `result` (dropped for tools not on the persistence
  // allowlist), and the previous inline logic dereferenced both unguarded.
  // The result path was the dangerous one: JSON.parse(call.result) threw into
  // a catch that then read call.result.length — a throw from inside the catch
  // handler, uncaught, unmounting the message tree.
  const view = deriveToolCallView(call);
  // W4/L5 — the summary is the ALWAYS-VISIBLE line on the collapsed card, and
  // it was rendering backend error strings verbatim (raw agent UUIDs, truncated
  // internals) as if they were the agent's work. Humanize the visible line only;
  // `detail` below still carries the raw string under "Result", one click away.
  // Spec: docs/superpowers/specs/2026-08-21-kill-lying-controls-design.md §4
  const resultSummary = humanizeToolSummary(view.summary, { isError: view.isError });
  const resultDetail = view.detail;

  return (
    <div className="rounded-xl border border-[var(--outline-variant)] bg-[var(--surface-container)] overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 hover:bg-[var(--surface-high)] transition-colors text-left"
      >
        <div className="shrink-0 w-7 h-7 rounded-lg bg-[var(--primary-muted)] flex items-center justify-center">
          <Wrench className="size-3.5 text-[var(--primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[var(--on-surface)]">
            {formatToolName(view.tool)}
          </p>
          {resultSummary && (
            <p className="text-[11px] text-[var(--on-surface-variant)] truncate mt-0.5">
              {resultSummary}
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="size-4 text-[var(--on-surface-variant)] shrink-0" />
        ) : (
          <ChevronRight className="size-4 text-[var(--on-surface-variant)] shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-[var(--outline-variant)] px-4 py-3 space-y-3">
          {view.hasInput && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)] mb-1.5">
                Input
              </p>
              <pre className="text-[11px] font-mono text-[var(--on-surface)] whitespace-pre-wrap break-all bg-[var(--surface-lowest)] rounded-lg p-3 border border-[var(--outline)]">
                {JSON.stringify(call.input ?? {}, null, 2)}
              </pre>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)] mb-1.5">
              Result
            </p>
            <pre className="text-[11px] font-mono text-[var(--on-surface)] whitespace-pre-wrap break-all bg-[var(--surface-lowest)] rounded-lg p-3 border border-[var(--outline)] max-h-48 overflow-y-auto">
              {resultDetail}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

/** Convert snake_case / camelCase tool names to readable labels */
function formatToolName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Research Source Cards (Stitch 3-column grid) ───────────────────────────

function SourceCards({ sources }: { sources: unknown[] }) {
  return (
    <div className="grid grid-cols-3 gap-3 mt-3">
      {sources.slice(0, 6).map((source, idx) => {
        const s = source as Record<string, unknown>;
        const category = (s.type as string) || (s.category as string) || `Source ${idx + 1}`;
        const title = (s.title as string) || (s.name as string) || (s.url as string) || "Reference";
        // Sources come from web search — attacker-controlled content, and now
        // persisted, which turns a transient reflected risk into a stored one.
        // Validate the scheme by parsing rather than pattern-matching: a regex
        // is defeated by embedded control characters (java&#9;script:) and
        // leading whitespace, both of which the DOM strips before navigating.
        const rawUrl = (s.url as string) || (s.link as string);
        const url = isSafeHttpUrl(rawUrl) ? rawUrl : undefined;
        // The snippet carries the actual excerpt (memory text, search result
        // context). It was collected by the backend and then never rendered —
        // so a memory source showed only its category and a title, and with the
        // title hardcoded to "Agent Memory" every card was identical and
        // content-free. Reported 2026-07-30.
        const snippet = typeof s.snippet === 'string' ? s.snippet.trim() : '';

        // Only linkable sources get link affordances. A memory has no URL by
        // design; rendering it as <a> with cursor-pointer + an ExternalLink
        // icon and then disabling it with pointer-events-none produced a card
        // that looked clickable and did nothing. Non-linkable sources are now
        // plain, static cards that don't pretend otherwise.
        const Card = url ? 'a' : 'div';

        return (
          <Card
            key={idx}
            {...(url
              ? { href: url, target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className={cn(
              "bg-[var(--surface-container)] rounded-xl border border-[var(--outline-variant)] p-3",
              "transition-all group",
              url && "hover:border-[var(--primary-muted)] hover:shadow-sm cursor-pointer"
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  "text-[var(--on-surface-variant)] transition-colors",
                  url && "group-hover:text-[var(--primary)]"
                )}>
                  {getCategoryIcon(category)}
                </span>
                <span className="text-[10px] font-bold text-[var(--on-surface-variant)] uppercase tracking-wide">
                  {category}
                </span>
              </div>
              {url && (
                <ExternalLink className="size-3 text-[var(--on-surface-variant)] opacity-0 group-hover:opacity-100 group-hover:text-[var(--primary)] transition-all" />
              )}
            </div>
            <p className="text-xs font-medium text-[var(--on-surface)] line-clamp-2 leading-relaxed">
              {title}
            </p>
            {snippet && (
              <p className="mt-1 text-[11px] text-[var(--on-surface-variant)] line-clamp-3 leading-relaxed">
                {snippet}
              </p>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ─── Decisions Table Card ────────────────────────────────────────────────────

interface DecisionRow {
  node: string;
  risk: number; // 0-100
  owner: string;
  deadline: string;
}

function DecisionsTable({ decisions, title, priority }: {
  decisions: DecisionRow[];
  title?: string;
  priority?: string;
}) {
  return (
    <div className="bg-[var(--surface-container)] rounded-2xl border border-[var(--outline-variant)] shadow-sm overflow-hidden mt-3">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--outline-variant)]">
        <p className="text-sm font-semibold text-[var(--on-surface)]">
          {title || "Today's Decisions"}
        </p>
        {priority && (
          <span className="bg-[var(--primary-muted)] text-[var(--primary)] text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide">
            {priority}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--outline-variant)]">
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)]">
                Decision Node
              </th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)]">
                Risk Score
              </th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)]">
                Owner
              </th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)]">
                Deadline
              </th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((row, idx) => (
              <tr
                key={idx}
                className={cn(
                  "border-b border-[var(--outline-variant)] last:border-b-0",
                  "hover:bg-[var(--surface-high)] transition-colors"
                )}
              >
                <td className="px-4 py-2.5 font-medium text-[var(--on-surface)]">
                  {row.node}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-[var(--surface-lowest)] overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          row.risk >= 70
                            ? "bg-red-500"
                            : row.risk >= 40
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                        )}
                        style={{ width: `${Math.min(100, Math.max(0, row.risk))}%` }}
                      />
                    </div>
                    <span className="text-[var(--on-surface-variant)] tabular-nums">
                      {row.risk}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[var(--on-surface-variant)]">{row.owner}</td>
                <td className="px-4 py-2.5 text-[var(--on-surface-variant)]">{row.deadline}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Inline action buttons ──────────────────────────────────────────────────

interface InlineAction {
  label: string;
  variant?: "primary" | "secondary";
}

function InlineActions({ actions }: { actions: InlineAction[] }) {
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {actions.map((action, idx) => (
        <button
          key={idx}
          className={cn(
            "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all",
            action.variant === "primary"
              ? "bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 shadow-sm"
              : "bg-[var(--surface-container)] text-[var(--on-surface)] border border-[var(--outline-variant)] hover:bg-[var(--surface-high)] hover:border-[var(--primary-muted)]"
          )}
        >
          {action.label}
          <ArrowRight className="size-3" />
        </button>
      ))}
    </div>
  );
}

// ─── Content parser: extract structured blocks from message + data ───────────

interface ParsedContent {
  textBlocks: string[];
  decisions: DecisionRow[] | null;
  decisionsMeta: { title?: string; priority?: string } | null;
  actions: InlineAction[];
  plan: OrchestrationPlanCardData | null;
}

/**
 * Parse a message's text content to detect structured data patterns:
 * - Decision tables from tool call results
 * - Action suggestions (lines starting with "Action:" or containing button-like text)
 */
function parseStructuredContent(
  content: string,
  toolCalls?: ToolCallLog[]
): ParsedContent {
  const result: ParsedContent = {
    textBlocks: [],
    decisions: null,
    decisionsMeta: null,
    actions: [],
    plan: null,
  };

  // Check tool call results for structured data (decisions tables)
  if (toolCalls && toolCalls.length > 0) {
    for (const tc of toolCalls) {
      // Skip calls whose payload was not persisted (tool not on the
      // persistence allowlist, or the metadata budget dropped it). Do NOT
      // skip merely-truncated results: truncation is structure-aware, so a
      // shortened JSON payload is still valid JSON and can still produce a
      // decisions table or plan card. Skipping those would cancel the exact
      // behaviour the write side works to preserve.
      if (typeof tc.result !== 'string' || tc.result === '') continue;
      try {
        const parsed = JSON.parse(tc.result);
        if (Array.isArray(parsed?.decisions)) {
          result.decisions = parsed.decisions.map(
            (d: Record<string, unknown>) => ({
              node: String(d.node || d.name || d.title || ""),
              risk: Number(d.risk || d.riskScore || d.risk_score || 0),
              owner: String(d.owner || d.assignee || ""),
              deadline: String(d.deadline || d.due || d.dueDate || ""),
            })
          );
          result.decisionsMeta = {
            title: parsed.title as string | undefined,
            priority: parsed.priority as string | undefined,
          };
        }
        // Orchestration plan preview (spec #4 Phase 1).
        // draft_orchestration_plan returns { plan_id, summary, candidates, note }.
        // A plan_id of null means "no template matched" — don't render a card.
        if (
          tc.tool === 'draft_orchestration_plan' &&
          parsed &&
          typeof parsed.plan_id === 'string' &&
          Array.isArray(parsed.candidates)
        ) {
          result.plan = {
            plan_id: parsed.plan_id,
            summary: typeof parsed.summary === 'string' ? parsed.summary : null,
            candidates: parsed.candidates
              .map((c: Record<string, unknown>) => ({
                template_id: String(c.template_id ?? ''),
                name: String(c.name ?? ''),
                score: Number(c.score ?? 0),
              }))
              .filter((c: { template_id: string }) => c.template_id),
            note: typeof parsed.note === 'string' ? parsed.note : null,
          };
        }
      } catch {
        // not JSON, skip
      }
    }
  }

  // Scan text for action patterns.
  //
  // The old `buttonPattern` (/^\*\*\[(.+?)\]\*\*$/) turned ANY line of the form
  // `**[x]**` into an action button. In a room, agents see other members'
  // messages prefixed `[agent-id]:` and imitate it — one wrote `**[auto-sales]**`
  // and the UI promoted it to a primary button that did nothing. Agent prose
  // must never be able to summon a control. Removed 2026-07-09; the explicit
  // `[ACTION: …]` opt-in remains. The line now falls through to textLines and
  // renders as bold `[x]` — the artifact's real cure is the backend prompt
  // format (companion PR), not this renderer.
  const lines = content.split("\n");
  const actionPattern = /^\[ACTION:\s*(.+?)\]$/i;
  const textLines: string[] = [];

  for (const line of lines) {
    const actionMatch = line.match(actionPattern);

    if (actionMatch) {
      result.actions.push({
        label: actionMatch[1],
        variant: result.actions.length === 0 ? "primary" : "secondary",
      });
    } else {
      textLines.push(line);
    }
  }

  // Remove trailing empty lines from text
  while (textLines.length > 0 && textLines[textLines.length - 1].trim() === "") {
    textLines.pop();
  }

  result.textBlocks = [textLines.join("\n")];
  return result;
}

// ─── Relative time ────────────────────────────────────────────────────────────

function relativeTime(ts: string | Date): string {
  const date = typeof ts === "string" ? new Date(ts) : ts;
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 10) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MessageBubbleProps {
  id?: string;
  role: "user" | "assistant" | "system";
  content: string;
  toolCalls?: ToolCallLog[];
  sources?: unknown[];
  thinking?: string;
  inputTokens?: number;
  outputTokens?: number;
  timestamp?: string | Date;
  agentName?: string;
  agentIcon?: string;
  /** Stable per-agent colour (rooms). Tints the avatar + name label. */
  agentColor?: string;
  /** Render `agentName` as a visible label above the message. Rooms set this;
   *  1:1 chat leaves it unset (one active agent needs no label). */
  showAgentName?: boolean;
  isStreaming?: boolean;
  /** Set when this user message failed to send. */
  error?: string;
  /** Re-send a failed user message. Rendered as a Retry control. */
  onRetry?: () => void;
  reaction?: "up" | "down" | null;
  onReaction?: (reaction: "up" | "down" | null) => void;
  onFeedback?: (text: string) => void;
  /** Pending approval decisions created by this turn. Renders inline
   *  approval cards below the message content.
   *  Per 2026-04-22-inline-approval-ux-design. */
  pendingDecisions?: PendingDecision[];
  /** Called after any approval card resolves. Parent replays the last
   *  user message so the agent re-runs and consumes the approval. */
  onApprovalResolved?: (result: { verb: "approved" | "denied"; scope?: "once" | "session" | "always" }) => void;
  /** Set when this turn's model was auto-downgraded to the included
   *  Ctrl-A model at zero prepaid balance. Renders a small inline note.
   *  Spec: docs/superpowers/specs/2026-07-01-v2-prepaid-credits-ui-design.md */
  modelDowngraded?: ModelDowngradeInfo;
  droppedToolCalls?: number;
  /** Set by the backend grounding gate when this answer was NOT verified against
   *  a knowledge-library lookup. Renders a caveat ABOVE the content.
   *  Audit F01; spec 2026-07-29-agent-grounding-safety-floor-design.md */
  ungrounded?: UngroundedInfo;
}

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Feedback pill options ───────────────────────────────────────────────────

const FEEDBACK_OPTIONS = [
  "Inaccurate",
  "Unhelpful",
  "Too long",
  "Off topic",
  "Other",
] as const;

// ─── Reaction bar for assistant messages ─────────────────────────────────────

function ReactionBar({
  reaction,
  onReaction,
  onFeedback,
}: {
  reaction: "up" | "down" | null;
  onReaction: (r: "up" | "down" | null) => void;
  onFeedback: (text: string) => void;
}) {
  const [showDownForm, setShowDownForm] = useState(false);
  const [showUpForm, setShowUpForm] = useState(false);
  const [selectedPills, setSelectedPills] = useState<string[]>([]);
  const [freeText, setFreeText] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const handleUp = () => {
    if (reaction === "up") {
      onReaction(null);
      setShowUpForm(false);
    } else {
      onReaction("up");
      setShowDownForm(false);
      setSelectedPills([]);
      setFreeText("");
      setToast("Thanks!");
      setTimeout(() => setToast(null), 1500);
    }
  };

  const handleDown = () => {
    if (reaction === "down") {
      onReaction(null);
      setShowDownForm(false);
    } else {
      onReaction("down");
      setShowUpForm(false);
      setShowDownForm(true);
    }
  };

  const handleSubmitDown = () => {
    const parts = [...selectedPills];
    if (freeText.trim()) parts.push(freeText.trim());
    onFeedback(parts.join(" | "));
    setShowDownForm(false);
    setToast("Feedback sent");
    setTimeout(() => setToast(null), 1500);
  };

  const handleSubmitUp = () => {
    if (freeText.trim()) onFeedback(freeText.trim());
    setShowUpForm(false);
    setToast("Thanks for sharing!");
    setTimeout(() => setToast(null), 1500);
  };

  const togglePill = (pill: string) => {
    setSelectedPills((prev) =>
      prev.includes(pill) ? prev.filter((p) => p !== pill) : [...prev, pill]
    );
  };

  return (
    <div className="mt-1.5">
      {/* Buttons row */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Thumbs up"
          onClick={handleUp}
          className={cn(
            "size-7 flex items-center justify-center rounded-md transition-colors",
            reaction === "up"
              ? "text-[var(--success)] bg-[var(--success)]/10"
              : "text-[var(--on-surface-muted)] hover:text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)]"
          )}
        >
          <ThumbsUp className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          onClick={handleDown}
          className={cn(
            "size-7 flex items-center justify-center rounded-md transition-colors",
            reaction === "down"
              ? "text-[var(--error)] bg-[var(--error)]/10"
              : "text-[var(--on-surface-muted)] hover:text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)]"
          )}
        >
          <ThumbsDown className="size-4" />
        </button>

        {/* Toast */}
        {toast && (
          <span className="text-xs text-[var(--on-surface-variant)] animate-[fade-in_0.15s_ease] ml-1">
            {toast}
          </span>
        )}

        {/* "Tell us what was good" link after thumbs up */}
        {reaction === "up" && !showUpForm && !toast && (
          <button
            type="button"
            onClick={() => { setShowUpForm(true); setFreeText(""); }}
            className="text-[11px] text-[var(--primary)] hover:underline ml-1 transition-colors"
          >
            Tell us what was good &rarr;
          </button>
        )}
      </div>

      {/* Thumbs-up feedback (simple text) */}
      {showUpForm && (
        <div className="bg-[var(--surface-container)] rounded-xl p-3 mt-2 animate-[fade-in_0.15s_ease] space-y-2">
          <input
            type="text"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="What did you like? (optional)"
            className="w-full bg-[var(--surface-lowest)] rounded-lg px-3 py-2 text-sm text-[var(--on-surface)] placeholder:text-[var(--on-surface-muted)] outline-none border border-transparent focus:border-[var(--primary-muted)]"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmitUp}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--primary)] text-white text-xs font-medium hover:opacity-90 transition-opacity"
            >
              <Send className="size-3" />
              Submit
            </button>
            <button
              type="button"
              onClick={() => setShowUpForm(false)}
              className="text-xs text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Thumbs-down feedback form */}
      {showDownForm && (
        <div className="bg-[var(--surface-container)] rounded-xl p-3 mt-2 animate-[fade-in_0.15s_ease] space-y-3">
          {/* Feedback pills */}
          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => togglePill(opt)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium transition-all",
                  selectedPills.includes(opt)
                    ? "bg-[var(--primary-muted)] text-[var(--primary)] ring-1 ring-[var(--primary)]"
                    : "bg-[var(--surface-high)] text-[var(--on-surface-variant)] hover:bg-[var(--primary-muted)] hover:text-[var(--primary)]"
                )}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Free-text input */}
          <input
            type="text"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Tell us more (optional)..."
            className="w-full bg-[var(--surface-lowest)] rounded-lg px-3 py-2 text-sm text-[var(--on-surface)] placeholder:text-[var(--on-surface-muted)] outline-none border border-transparent focus:border-[var(--primary-muted)]"
          />

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmitDown}
              disabled={selectedPills.length === 0 && !freeText.trim()}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                selectedPills.length > 0 || freeText.trim()
                  ? "bg-[var(--primary)] text-white hover:opacity-90"
                  : "bg-[var(--surface-high)] text-[var(--on-surface-muted)] cursor-not-allowed"
              )}
            >
              <Send className="size-3" />
              Submit
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDownForm(false);
                setSelectedPills([]);
                setFreeText("");
              }}
              className="text-xs text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MessageBubble({
  id,
  role,
  content,
  toolCalls: rawToolCalls,
  sources: rawSources,
  thinking,
  inputTokens,
  outputTokens,
  timestamp,
  agentName = "Agent",
  agentIcon,
  agentColor,
  showAgentName,
  isStreaming,
  error,
  onRetry,
  reaction = null,
  onReaction,
  onFeedback,
  pendingDecisions,
  onApprovalResolved,
  modelDowngraded,
  droppedToolCalls,
  ungrounded,
}: MessageBubbleProps) {
  // Normalise the CONTAINERS, not just their elements.
  //
  // deriveToolCallView is total per tool call, but every render site guards
  // with `toolCalls && toolCalls.length > 0` — which a string passes ("hello"
  // has length 5) before `.map` comes back undefined and the TypeError unmounts
  // the message tree. The DB path validates with Array.isArray in the messages
  // route; the live WebSocket path (chat-context casts raw socket JSON straight
  // to ToolCallLog[]) does not, so the guarantee has to be restated here, at
  // the render boundary both paths share.
  const toolCalls = Array.isArray(rawToolCalls) ? rawToolCalls : [];
  const sources = Array.isArray(rawSources) ? rawSources : [];

  const isUser = role === "user";
  const ts = timestamp ? relativeTime(timestamp) : null;

  if (isUser) {
    return (
      <div className="flex items-start gap-3 justify-end animate-[fade-in_0.2s_ease_both]">
        <div className="max-w-[85%]">
          <div
            className={cn(
              "rounded-2xl rounded-tr-sm px-4 py-2.5",
              error
                ? "bg-[var(--surface-container)] border border-[var(--error)]"
                : "bg-[var(--primary-muted)]"
            )}
          >
            <p className="text-sm text-[var(--on-surface)] leading-relaxed whitespace-pre-wrap">
              {content}
            </p>
          </div>
          {/* Send failure. Without this the message looks delivered and the
              user re-types it. `error` has always been set on the row; until
              now nothing read it. */}
          {error && (
            <div className="flex items-center justify-end gap-2 mt-1">
              <span className="text-[10px] text-[var(--error)]">
                Not sent — {error}
              </span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--primary)] hover:underline"
                >
                  <RotateCcw className="size-3" />
                  Retry
                </button>
              )}
            </div>
          )}
          {ts && !error && (
            <p className="text-[10px] text-[var(--on-surface-muted)] mt-1 text-right">{ts}</p>
          )}
        </div>
        {/* User avatar */}
        <div className="shrink-0 w-6 h-6 rounded-full bg-[var(--surface-bright)] flex items-center justify-center mt-0.5">
          <span className="text-[10px] font-bold text-[var(--on-surface-variant)]">You</span>
        </div>
      </div>
    );
  }

  // Show more/less state for long assistant messages
  const [isExpanded, setIsExpanded] = useState(false);
  const isLong = !isUser && role !== "system" && content.length > 600;

  // Collapsible panel states
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);

  // Read-aloud (Web Speech API — see src/lib/speech.ts)
  const {
    supported: speechSupported,
    speakingId,
    voices,
    selectedVoice,
    speak,
    selectVoice,
  } = useSpeech();
  // Playback is global, so "is something speaking" is not "am I speaking".
  // Without this every bubble in the thread renders Stop at once.
  const speaking = speakingId != null && speakingId === id;

  // System message
  if (role === "system") {
    return (
      <div className="flex justify-center animate-[fade-in_0.2s_ease_both]">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--surface-container)] rounded-full">
          <AlertTriangle className="size-3 text-[var(--on-surface-variant)]" />
          <span className="text-xs text-[var(--on-surface-variant)]">{content}</span>
        </div>
      </div>
    );
  }

  // Parse structured content from the message
  const parsed = parseStructuredContent(content, toolCalls);

  // Assistant message
  return (
    <div data-testid="assistant-message" className="flex items-start gap-3 animate-[fade-in_0.2s_ease_both]">
      {/* Avatar — small bot icon. In rooms, `agentColor` gives each member a
          stable swatch so N concurrent replies aren't N identical circles. */}
      <div
        className={cn(
          "shrink-0 w-6 h-6 rounded-md flex items-center justify-center mt-0.5",
          !agentColor && "bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)]"
        )}
        style={agentColor ? { backgroundColor: agentColor } : undefined}
      >
        {agentIcon ? (
          <span className="text-[10px]">{agentIcon}</span>
        ) : (
          <span className="text-[10px] font-bold text-white">
            {agentName.slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>

      <div className="flex-1 max-w-[85%] space-y-3">
        {/* Who is speaking. Rooms only — 1:1 chat has one active agent and
            leaves `showAgentName` unset, so this never renders there. */}
        {showAgentName && (
          <p
            data-testid="agent-name-label"
            className="text-xs font-semibold leading-none"
            style={agentColor ? { color: agentColor } : undefined}
          >
            {agentName}
          </p>
        )}

        {/* Collapsible content area for long messages */}
        <div className="relative">
          <div
            className={cn(
              isLong && !isExpanded && "max-h-[240px] overflow-hidden"
            )}
          >
            {/* Tool call cards (shown before response text) */}
            {toolCalls && toolCalls.length > 0 && (
              <div className="space-y-2 mb-3">
                {toolCalls.map((tc, i) => (
                  <ToolCallCard key={i} call={tc} />
                ))}
                {/* Disclose the shortfall. The write side caps stored calls at
                    50; without this the user sees a partial list presented as
                    the complete record of what the agent did. */}
                {typeof droppedToolCalls === "number" && droppedToolCalls > 0 && (
                  <p className="text-[11px] text-[var(--on-surface-muted)] italic px-1">
                    {droppedToolCalls} more tool call{droppedToolCalls === 1 ? "" : "s"} not stored.
                  </p>
                )}
              </div>
            )}

            {/* Grounding caveat — MUST render ABOVE the content, never below.
                A caveat placed after a long answer can be scrolled away from the
                claim it qualifies, which defeats the point.

                Audit F01: an agent asked for a canary phrase planted in an
                uploaded document returned a fabricated value in quotes with no
                hedge. The backend gate now detects that shape; this is the only
                place the user learns about it.

                Deliberately a WARNING, not an ERROR: we do not yet know that
                retrieval is broken (root cause still unproven), so the copy says
                "not verified" rather than asserting a failure. Not dismissible —
                the caveat is as durable as the claim.
                Spec: 2026-07-29-agent-grounding-safety-floor-design.md */}
            {ungrounded && !isStreaming && (
              <div
                role="note"
                className="flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] leading-relaxed"
                style={{
                  background: "var(--warning-surface, rgba(234,179,8,0.10))",
                  border: "1px solid var(--warning, #eab308)",
                  color: "var(--on-surface-variant)",
                }}
              >
                <AlertTriangle
                  className="size-3.5 shrink-0 mt-px"
                  style={{ color: "var(--warning, #eab308)" }}
                  aria-hidden="true"
                />
                <span>
                  <span className="font-semibold" style={{ color: "var(--on-surface)" }}>
                    Not verified against your library.
                  </span>{" "}
                  This answer wasn&apos;t grounded in a document lookup — treat any specific
                  values as unconfirmed.{" "}
                  <Link
                    href="/knowledge"
                    className="font-semibold underline"
                    style={{ color: "var(--warning, #eab308)" }}
                  >
                    Open Knowledge Library
                  </Link>
                </span>
              </div>
            )}

            {/* Message content — no bubble, just text */}
            <div className="space-y-1">
              {isStreaming && !content ? (
                <TypingDots />
              ) : (
                parsed.textBlocks.map((block, idx) => (
                  <div key={idx}>{renderMarkdown(block)}</div>
                ))
              )}
              {isStreaming && content && (
                <span className="inline-block w-1 h-4 bg-[var(--primary)] animate-pulse ml-0.5 align-middle" />
              )}
            </div>

            {/* Decisions Table (from structured tool call data) */}
            {parsed.decisions && parsed.decisions.length > 0 && (
              <DecisionsTable
                decisions={parsed.decisions}
                title={parsed.decisionsMeta?.title}
                priority={parsed.decisionsMeta?.priority}
              />
            )}

            {/* Orchestration plan preview (spec #4 Phase 1) */}
            {parsed.plan && <OrchestrationPlanCard plan={parsed.plan} />}

            {/* Sources render ONCE, in the collapsible "Sources (N)" panel below.
                There used to be a second, unconditional <SourceCards> here — so
                expanding Sources showed every card twice (reported 2026-07-30:
                two identical "Agent Memory" cards on one turn). The collapsible
                copy wins because it is labelled and carries the count. */}

            {/* Inline action buttons */}
            {parsed.actions.length > 0 && (
              <InlineActions actions={parsed.actions} />
            )}
          </div>

          {/* Fade gradient when collapsed */}
          {isLong && !isExpanded && (
            <div className="bg-gradient-to-t from-[var(--surface)] to-transparent h-12 absolute bottom-0 left-0 right-0 pointer-events-none" />
          )}
        </div>

        {/* Model-downgrade note — this turn was auto-downgraded from the
            requested prepaid model to the included Ctrl-A model at zero
            balance. Spec: 2026-07-01-v2-prepaid-credits-ui-design.md */}
        {/* Branches on `reason`. This previously asserted "top up credits" for
            every downgrade including route_unavailable — telling a user with a
            healthy balance they were out of credit. Harmless while the banner
            was transient; persisting it makes a wrong billing claim durable. */}
        {modelDowngraded && (
          <p className="text-[11px] text-[var(--on-surface-muted)] italic">
            {modelDowngraded.reason === "insufficient_credits"
              ? `Answered with Ctrl-A — top up credits to use ${modelDowngraded.requested}.`
              : `Answered with Ctrl-A — ${modelDowngraded.requested} was unavailable.`}
          </p>
        )}

        {/* Show more / Show less button */}
        {isLong && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="text-xs text-[var(--primary)] font-medium hover:text-[var(--primary-bright)] cursor-pointer py-1 flex items-center gap-1"
            >
              {isExpanded ? (
                <>Show less <ChevronUp className="size-3" /></>
              ) : (
                <>Show more <ChevronDown className="size-3" /></>
              )}
            </button>
          </div>
        )}

        {/* Collapsible toggle panels */}
        {role === "assistant" && !isStreaming && (
          <div className="space-y-2">
            {/* Toggle buttons row */}
            <div className="flex items-center gap-1 flex-wrap">
              {thinking && thinking.trim() && (
                <button
                  type="button"
                  onClick={() => setThinkingOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] hover:bg-[var(--surface-high)] transition-colors cursor-pointer"
                >
                  <Brain className="size-3" />
                  Thinking
                  {thinkingOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                </button>
              )}
              {toolCalls && toolCalls.length > 0 && (
                <button
                  type="button"
                  onClick={() => setToolsOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] hover:bg-[var(--surface-high)] transition-colors cursor-pointer"
                >
                  <Wrench className="size-3" />
                  Tools ({toolCalls.length})
                  {toolsOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                </button>
              )}
              {sources && sources.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSourcesOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] hover:bg-[var(--surface-high)] transition-colors cursor-pointer"
                >
                  <Paperclip className="size-3" />
                  Sources ({sources.length})
                  {sourcesOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                </button>
              )}
            </div>

            {/* Thinking panel */}
            {thinkingOpen && thinking && thinking.trim() && (
              <div className="bg-[var(--surface-lowest)] rounded-lg p-3 font-mono text-xs text-[var(--on-surface-variant)] leading-relaxed whitespace-pre-wrap">
                {thinking}
                {(inputTokens != null || outputTokens != null) && (
                  <div className="mt-3 pt-2 border-t border-[var(--outline-variant)] text-[10px] text-[var(--on-surface-muted)]">
                    <span className="font-bold">--- Token Usage ---</span>
                    {inputTokens != null && (
                      <div>Input: {inputTokens.toLocaleString()}</div>
                    )}
                    {outputTokens != null && (
                      <div>Output: {outputTokens.toLocaleString()}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tools panel */}
            {toolsOpen && toolCalls && toolCalls.length > 0 && (
              <div className="space-y-2">
                {toolCalls.map((tc, i) => (
                  <ToolCallCard key={i} call={tc} />
                ))}
              </div>
            )}

            {/* Sources panel */}
            {sourcesOpen && sources && sources.length > 0 && (
              <SourceCards sources={sources} />
            )}
          </div>
        )}

        {ts && (
          <p className="text-[10px] text-[var(--on-surface-muted)] mt-1">{ts}</p>
        )}

        {/* Inline approval cards — one per pending decision the agent opened */}
        {role === "assistant" && pendingDecisions && pendingDecisions.length > 0 && (
          <div className="space-y-2">
            {pendingDecisions.map(d => (
              <ApprovalCard key={d.id} decision={d} onResolved={onApprovalResolved} />
            ))}
          </div>
        )}

        {/* Per-message actions — assistant messages only, not while streaming.
            Read-aloud lives here rather than in the toggle row above: this is
            where per-message actions already are, so it is where a hand looks. */}
        {role === "assistant" && !isStreaming && (
          <div className="flex items-center gap-1 flex-wrap">
            {onReaction && onFeedback && (
              <ReactionBar
                reaction={reaction}
                onReaction={onReaction}
                onFeedback={onFeedback}
              />
            )}
          {speechSupported && (
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => speak(stripMarkdownForSpeech(content), id)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] hover:bg-[var(--surface-high)] transition-colors cursor-pointer"
                aria-label={speaking ? "Stop reading aloud" : "Read reply aloud"}
              >
                {speaking ? <Square className="size-3" /> : <Volume2 className="size-3" />}
                {speaking ? "Stop" : "Listen"}
              </button>
              {voices.length > 0 && (
                <button
                  type="button"
                  onClick={() => setVoicePickerOpen((v) => !v)}
                  className="flex items-center px-1 py-1 rounded-md text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] hover:bg-[var(--surface-high)] transition-colors cursor-pointer"
                  aria-label="Choose read-aloud voice"
                >
                  <ChevronDown className="size-3" />
                </button>
              )}
              {voicePickerOpen && voices.length > 0 && (
                <div className="absolute z-10 top-full left-0 mt-1 max-h-64 overflow-y-auto bg-[var(--surface-container)] border border-[var(--outline-variant)] rounded-lg shadow-lg py-1 min-w-[240px]">
                  {!voices.some((v) => voiceQuality(v).label) && (
                    <p className="px-3 py-2 text-[10px] leading-snug text-[var(--on-surface-muted)] border-b border-[var(--outline-variant)]">
                      For better quality, download an Enhanced or Premium voice in
                      your device&apos;s accessibility settings — it appears here
                      automatically.
                    </p>
                  )}
                  {groupVoicesByLanguage(voices).map(([lang, group]) => (
                    <div key={lang}>
                      <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-[var(--on-surface-muted)]">
                        {lang}
                      </div>
                      {group.map((voice) => (
                        <button
                          key={voice.voiceURI}
                          type="button"
                          onClick={() => {
                            selectVoice(voice.voiceURI);
                            setVoicePickerOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-[var(--on-surface)] hover:bg-[var(--surface-high)] cursor-pointer"
                        >
                          <span className="flex-1 truncate">{voice.name}</span>
                          {voiceQuality(voice).label && (
                            <span className="shrink-0 px-1 py-0.5 rounded text-[9px] font-medium uppercase tracking-wide bg-[var(--surface-high)] text-[var(--on-surface-variant)]">
                              {voiceQuality(voice).label}
                            </span>
                          )}
                          {selectedVoice?.voiceURI === voice.voiceURI && <Check className="size-3" />}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Typing indicator (fallback) ──────────────────────────────────────────────

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-[var(--on-surface-variant)] animate-bounce"
          style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.9s" }}
        />
      ))}
    </div>
  );
}
